import React, { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { INVOICES, computeInterest, evidenceScore } from "../data/mock";
import { inr, fmtDate } from "../lib/format";
import { InterestEngine } from "./InterestEngine";
import { EvidencePanel } from "./EvidencePanel";
import { StageTracker } from "./StageTracker";
import { DemandNotice } from "./DemandNotice";
import { AlertTriangle } from "lucide-react";

export const DemoWorkspace = () => {
  const [activeId, setActiveId] = useState(INVOICES[0].id);
  const invoice = useMemo(() => INVOICES.find((i) => i.id === activeId), [activeId]);
  const calc = useMemo(() => computeInterest(invoice), [invoice]);

  return (
    <section id="demo" className="border-b border-border bg-white">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">
              Live demo · sample data
            </span>
            <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight text-ink">
              Explore a real case file
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              This is Baaki's working prototype running on mock invoices. Pick a case, then
              switch tabs to see the statutory interest breakdown, the evidence score, the
              stage tracker, and the generated demand notice.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 bg-danger-bg border border-danger/30 px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-danger" strokeWidth={1.5} />
            <span className="text-xs text-ink">Demo figures — not legal advice</span>
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 border border-border">
          {/* Sidebar: invoice list */}
          <aside className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-border bg-paper">
            <div className="px-4 py-3 border-b border-border font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              Overdue invoices ({INVOICES.length})
            </div>
            <ul>
              {INVOICES.map((inv) => {
                const c = computeInterest(inv);
                const active = inv.id === activeId;
                return (
                  <li key={inv.id}>
                    <button
                      data-testid={`invoice-row-${inv.id}`}
                      onClick={() => setActiveId(inv.id)}
                      className={`w-full text-left px-4 py-4 border-b border-border transition-colors duration-150 ${
                        active ? "bg-white ring-1 ring-inset ring-signal" : "hover:bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-signal">{inv.id}</span>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5">
                          {inv.status}
                        </span>
                      </div>
                      <div className="mt-1.5 text-sm font-medium text-ink truncate">{inv.buyer}</div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="font-mono text-sm text-ink">{inr(inv.principal)}</span>
                        <span className="font-mono text-[11px] text-danger">{c.daysOverdue}d overdue</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Main workspace */}
          <div className="lg:col-span-8 bg-white">
            <div className="px-5 py-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-serif text-2xl font-semibold text-ink">{invoice.buyer}</div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-0.5">
                    {invoice.id} · Invoiced {fmtDate(invoice.invoiceDate)} · Accepted {fmtDate(invoice.acceptanceDate)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Case strength</div>
                  <div className="font-mono text-lg font-semibold text-signal">{evidenceScore(invoice.evidence)}/100</div>
                </div>
              </div>
            </div>

            <Tabs defaultValue="interest" className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b border-border bg-paper h-auto p-0">
                {[
                  ["interest", "Interest Engine"],
                  ["evidence", "Evidence"],
                  ["tracker", "Case Tracker"],
                  ["notice", "Demand Notice"],
                ].map(([v, label]) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`demo-tab-${v}`}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-signal data-[state=active]:text-signal data-[state=active]:bg-white data-[state=active]:shadow-none px-4 py-3 text-sm font-medium"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="interest" className="p-5 mt-0">
                <InterestEngine invoice={invoice} calc={calc} />
              </TabsContent>
              <TabsContent value="evidence" className="p-5 mt-0">
                <EvidencePanel evidence={invoice.evidence} />
              </TabsContent>
              <TabsContent value="tracker" className="p-6 mt-0">
                <StageTracker activeIndex={invoice.stageIndex} />
              </TabsContent>
              <TabsContent value="notice" className="mt-0">
                <DemandNotice invoice={invoice} calc={calc} companyName="Nandi Precision Works" />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};
